import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Copy, TrendingUp, TrendingDown, Users, Award, 
  Activity, Play, Square, CheckCircle2, Loader2, Zap, Star, AlertTriangle, ArrowDownCircle, Lock
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Spanish number formatter
const formatEUR = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0,00€';
  return value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
};

export default function CopyTrading() {
  const [stats, setStats] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [livePrices, setLivePrices] = useState({});
  const [activeTrades, setActiveTrades] = useState([]);

  // Fetch live prices from the API
  const fetchLivePrices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crypto/prices`);
      const pricesData = res.data.prices || res.data || [];
      const pricesMap = {};
      pricesData.forEach(coin => {
        pricesMap[coin.id] = coin.current_price;
      });
      setLivePrices(pricesMap);
      
      // Generate realistic trades based on live prices
      if (Object.keys(pricesMap).length > 0) {
        const trades = [
          { id: 1, symbol: 'BTC', coinId: 'bitcoin', entryOffset: 0.023 },
          { id: 2, symbol: 'ETH', coinId: 'ethereum', entryOffset: 0.018 },
          { id: 3, symbol: 'SOL', coinId: 'solana', entryOffset: 0.031 },
          { id: 4, symbol: 'BNB', coinId: 'binancecoin', entryOffset: 0.015 },
        ];
        
        const tradesWithPrices = trades.map(trade => {
          const currentPrice = pricesMap[trade.coinId] || 0;
          const entryPrice = currentPrice * (1 - trade.entryOffset);
          const profitPercent = ((currentPrice - entryPrice) / entryPrice * 100);
          
          return {
            ...trade,
            entry_price: entryPrice,
            current_price: currentPrice,
            profit_percent: profitPercent
          };
        });
        
        setActiveTrades(tradesWithPrices);
      }
    } catch (e) {
      console.error('Error fetching prices:', e);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      // Cargar cada endpoint por separado para mayor resiliencia
      const [tradesRes, portfolioRes] = await Promise.all([
        axios.get(`${API}/broker/trades`).catch(() => ({ data: { stats: {} } })),
        axios.get(`${API}/portfolio`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      if (tradesRes.data.stats) setStats(tradesRes.data.stats);
      if (portfolioRes.data) setPortfolio(portfolioRes.data);
    } catch (e) {
      console.error("Error cargando datos:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchLivePrices();
    const interval = setInterval(() => {
      fetchData();
      fetchLivePrices();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData, fetchLivePrices]);

  const handleCopyToggle = async () => {
    if (portfolio?.balance === 0) {
      toast.error("Necesitas depositar fondos para comenzar");
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const endpoint = portfolio?.is_copying ? 'stop-copy' : 'copy';
      await axios.post(`${API}/portfolio/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(portfolio?.is_copying ? 'Copy trading detenido' : '¡Copy trading iniciado! Tu dinero está generando ganancias.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FCD535] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="copy-trading-page">
      {/* Broker Profile Card */}
      <Card className="card-dark overflow-hidden">
        <div className="bg-gradient-to-r from-[#FCD535]/10 to-[#0ECB81]/10">
          <CardContent className="p-3 md:p-6">
            <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
              {/* Broker Info */}
              <div className="flex items-start gap-3 md:gap-4">
                <div className="relative flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_15d8ac38-293d-45ed-a585-187f28b97866/artifacts/bpkxytwu_IMG_9072.jpeg"
                    alt="Andres Martin"
                    className="w-14 h-14 md:w-24 md:h-24 rounded-full object-cover object-top border-2 md:border-4 border-[#FCD535]"
                  />
                  <span className="absolute -bottom-1 -right-1 w-4 h-4 md:w-6 md:h-6 bg-[#0ECB81] rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-white" />
                  </span>
                </div>
                <div className="flex-1 min-w-0 space-y-1 md:space-y-2">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base md:text-2xl font-bold text-[#EAECEF]">Andres Martin</h2>
                    <Badge className="bg-[#0ECB81]/20 text-[#0ECB81] border-0 text-[10px] md:text-xs px-1 md:px-2">
                      <Star className="w-2 h-2 md:w-3 md:h-3 mr-1 fill-current hidden md:inline" />
                      Top Trader
                    </Badge>
                  </div>
                  <p className="text-[11px] md:text-sm text-[#848E9C]">Senior Broker • 8 años de experiencia</p>
                  <p className="text-xs md:text-sm text-[#848E9C] max-w-md hidden md:block">
                    Especialista en análisis técnico y trading de alta frecuencia. 
                    Más de 2M€ gestionados con un historial probado de éxito.
                  </p>
                </div>
              </div>
              
              {/* Action Button */}
              <div className="lg:ml-auto flex flex-col items-center lg:items-end gap-3 md:gap-4">
                {!portfolio?.is_copying ? (
                  <Button 
                    onClick={() => {
                      if (portfolio?.copy_trading_blocked) {
                        // Scroll al mensaje de bloqueo
                        document.getElementById('blocked-message')?.scrollIntoView({ behavior: 'smooth' });
                        toast.error('Debes realizar un nuevo depósito para continuar');
                      } else {
                        handleCopyToggle();
                      }
                    }}
                    disabled={actionLoading || (!portfolio?.copy_trading_blocked && portfolio?.balance < 50)}
                    className="w-full md:w-auto py-3 px-6 md:py-8 md:px-10 text-sm md:text-xl font-bold bg-gradient-to-r from-[#FCD535] via-[#F0B90B] to-[#FCD535] hover:from-[#F0B90B] hover:via-[#FCD535] hover:to-[#F0B90B] text-[#0B0E11] shadow-lg md:shadow-2xl md:shadow-[#FCD535]/40 transition-all hover:scale-105 md:animate-pulse md:hover:animate-none"
                    data-testid="copy-toggle-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 md:w-6 md:h-6 animate-spin mr-2" />
                    ) : (
                      <>
                        <Zap className="w-4 h-4 md:w-7 md:h-7 mr-2 md:mr-3 md:animate-bounce" />
                        <span>COMENZAR A COPIAR</span>
                      </>
                    )}
                  </Button>
                ) : (
                  <Button 
                    onClick={handleCopyToggle}
                    disabled={actionLoading}
                    className="w-full md:w-auto py-3 px-6 md:py-6 md:px-8 text-sm md:text-lg font-bold bg-[#F6465D] hover:bg-[#F6465D]/90 text-white"
                    data-testid="copy-toggle-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin mr-2" />
                    ) : (
                      <Square className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    )}
                    Detener Copy Trading
                  </Button>
                )}
                
                {portfolio?.is_copying && !portfolio?.copy_trading_blocked && (
                  <div className="flex items-center gap-2 text-[#0ECB81] text-xs md:text-base md:bg-[#0ECB81]/10 md:px-4 md:py-2 md:rounded-full">
                    <span className="w-2 h-2 md:w-3 md:h-3 bg-[#0ECB81] rounded-full animate-pulse" />
                    <span className="md:font-semibold">Generando ganancias...</span>
                  </div>
                )}
                
                {portfolio?.balance < 50 && !portfolio?.is_copying && !portfolio?.copy_trading_blocked && (
                  <p className="text-xs md:text-sm text-[#F0B90B]">Mínimo 50€ para comenzar</p>
                )}
              </div>
            </div>
          </CardContent>
        </div>
        
        {/* Performance Stats */}
        <CardContent className="p-0">
          <div className="grid grid-cols-4 divide-x divide-[#2B3139]">
            <div className="p-2 md:p-4 text-center">
              <p className="text-sm md:text-2xl font-bold text-[#0ECB81]">92,3%</p>
              <p className="text-[9px] md:text-xs text-[#848E9C]">Win Rate</p>
            </div>
            <div className="p-2 md:p-4 text-center">
              <p className="text-sm md:text-2xl font-bold text-[#0ECB81]">+847%</p>
              <p className="text-[9px] md:text-xs text-[#848E9C]">Rent. Anual</p>
            </div>
            <div className="p-2 md:p-4 text-center">
              <p className="text-sm md:text-2xl font-bold text-[#EAECEF]">2.847</p>
              <p className="text-[9px] md:text-xs text-[#848E9C]">Operaciones</p>
            </div>
            <div className="p-2 md:p-4 text-center">
              <p className="text-sm md:text-2xl font-bold text-[#EAECEF]">15.432</p>
              <p className="text-[9px] md:text-xs text-[#848E9C]">Copiadores</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Copy Trading Blocked Message */}
      {portfolio?.copy_trading_blocked && (
        <Card id="blocked-message" className="card-dark border-2 border-[#FCD535] bg-gradient-to-b from-[#181A20] to-[#0B0E11]">
          <CardContent className="p-5 md:p-8 text-center">
            {/* Icon */}
            <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#FCD535]/20 to-[#F0B90B]/10 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-[#FCD535]/30">
              <Lock className="w-8 h-8 md:w-10 md:h-10 text-[#FCD535]" />
            </div>
            
            {/* Title */}
            <h3 className="text-xl md:text-2xl font-bold text-[#EAECEF] mb-1">Inversión Completada</h3>
            <p className="text-[#0ECB81] font-semibold text-sm md:text-base mb-4">¡Enhorabuena por tus ganancias!</p>
            
            {/* Message Box */}
            <div className="bg-[#0B0E11] rounded-xl p-4 md:p-5 mb-4 border border-[#FCD535]/30 max-w-xl mx-auto">
              <p className="text-[#EAECEF] text-sm md:text-base leading-relaxed mb-3">
                {portfolio?.copy_block_message || "Tu inversión actual ha finalizado. Para seguir copiando operaciones y generando ganancias, realiza un nuevo depósito."}
              </p>
              <div className="bg-[#FCD535]/10 rounded-lg p-3 border border-[#FCD535]/20">
                <p className="text-[#FCD535] font-bold text-xs md:text-sm">
                  💡 Nuevo depósito = Nueva inversión = Nuevas ganancias
                </p>
              </div>
            </div>
            
            {/* CTA Button */}
            <Button 
              onClick={() => window.location.href = '/deposit'}
              className="w-full md:w-auto bg-gradient-to-r from-[#FCD535] to-[#F0B90B] hover:from-[#F0B90B] hover:to-[#FCD535] text-[#0B0E11] font-bold py-4 md:py-5 px-8 md:px-12 text-base md:text-lg rounded-xl shadow-lg shadow-[#FCD535]/20 transition-all hover:scale-105"
              data-testid="new-deposit-btn"
            >
              💰 HACER NUEVO DEPÓSITO
            </Button>
            
            <p className="text-[10px] md:text-xs text-[#848E9C] mt-4">
              ¿Dudas? Contacta con tu asesor personal
            </p>
          </CardContent>
        </Card>
      )}

      {/* Big CTA if not copying */}
      {!portfolio?.is_copying && portfolio?.balance >= 50 && !portfolio?.copy_trading_blocked && (
        <Card className="card-dark border-2 border-[#FCD535] bg-gradient-to-r from-[#FCD535]/5 to-[#0ECB81]/5">
          <CardContent className="p-8 text-center">
            <Zap className="w-16 h-16 text-[#FCD535] mx-auto mb-4 animate-pulse" />
            <h3 className="text-2xl font-bold text-[#EAECEF] mb-2">¡Tu dinero está listo para trabajar!</h3>
            <p className="text-[#848E9C] mb-6 max-w-xl mx-auto">
              Tienes <span className="text-[#0ECB81] font-bold">{formatEUR(portfolio?.balance)}</span> disponibles. 
              Activa el copy trading y empieza a generar ganancias automáticamente siguiendo a Andres Martin.
            </p>
            <Button 
              onClick={handleCopyToggle}
              disabled={actionLoading}
              className="py-8 px-16 text-2xl font-bold bg-gradient-to-r from-[#0ECB81] to-[#00D68F] hover:from-[#00D68F] hover:to-[#0ECB81] text-white shadow-2xl shadow-[#0ECB81]/40 transition-all hover:scale-105"
              data-testid="big-copy-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-7 h-7 animate-spin mr-3" />
              ) : (
                <Play className="w-7 h-7 mr-3" />
              )}
              ACTIVAR COPY TRADING AHORA
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Not enough balance warning */}
      {!portfolio?.is_copying && portfolio?.balance > 0 && portfolio?.balance < 50 && !portfolio?.copy_trading_blocked && (
        <Card className="card-dark border-2 border-[#F6465D]">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-[#F6465D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-[#F6465D]" />
            </div>
            <h3 className="text-2xl font-bold text-[#EAECEF] mb-2">Balance Insuficiente</h3>
            <p className="text-[#848E9C] mb-4 max-w-xl mx-auto">
              Tienes <span className="text-[#FCD535] font-bold">{formatEUR(portfolio?.balance)}</span> pero necesitas un mínimo de <span className="text-[#0ECB81] font-bold">50€</span> para iniciar el Copy Trading.
            </p>
            <div className="bg-[#0B0E11] rounded-lg p-4 mb-6 border border-[#2B3139] max-w-md mx-auto">
              <p className="text-sm text-[#848E9C] mb-2">Te faltan:</p>
              <p className="text-3xl font-bold font-mono text-[#F6465D]">{formatEUR(50 - portfolio?.balance)}</p>
            </div>
            <p className="text-sm text-[#FCD535] mb-6">
              💡 <strong>Recomendación:</strong> Deposita 100€ para generar hasta 5 veces más ganancias
            </p>
            <Link to="/deposit">
              <Button className="btn-primary py-6 px-12 text-lg font-bold">
                <ArrowDownCircle className="w-5 h-5 mr-2" />
                Depositar Fondos
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid - Responsive */}
      <div className="grid grid-cols-5 md:grid-cols-5 gap-1 md:gap-4">
        <Card className="card-dark">
          <CardContent className="p-1.5 md:p-4 text-center">
            <Award className="w-3 h-3 md:w-6 md:h-6 text-[#FCD535] mx-auto mb-0.5 md:mb-2" />
            <p className="text-xs md:text-2xl font-bold font-mono text-[#0ECB81]" data-testid="win-rate">
              {stats?.win_rate || 92}%
            </p>
            <p className="text-[8px] md:text-xs text-[#848E9C]">Win Rate</p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-1.5 md:p-4 text-center">
            <TrendingUp className="w-3 h-3 md:w-6 md:h-6 text-[#0ECB81] mx-auto mb-0.5 md:mb-2" />
            <p className="text-xs md:text-2xl font-bold font-mono text-[#EAECEF]" data-testid="total-trades">
              <span className="md:hidden">2.8K</span>
              <span className="hidden md:inline">2.847</span>
            </p>
            <p className="text-[8px] md:text-xs text-[#848E9C]">Operaciones</p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-1.5 md:p-4 text-center">
            <Activity className="w-3 h-3 md:w-6 md:h-6 text-[#FCD535] mx-auto mb-0.5 md:mb-2" />
            <p className="text-xs md:text-2xl font-bold font-mono text-[#0ECB81]" data-testid="avg-profit">
              +{stats?.avg_profit_per_trade || 5.2}%
            </p>
            <p className="text-[8px] md:text-xs text-[#848E9C]">Promedio</p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-1.5 md:p-4 text-center">
            <Users className="w-3 h-3 md:w-6 md:h-6 text-[#FCD535] mx-auto mb-0.5 md:mb-2" />
            <p className="text-xs md:text-2xl font-bold font-mono text-[#EAECEF]" data-testid="followers">
              <span className="md:hidden">15K</span>
              <span className="hidden md:inline">15.432</span>
            </p>
            <p className="text-[8px] md:text-xs text-[#848E9C]">Copiadores</p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-1.5 md:p-4 text-center">
            <TrendingUp className="w-3 h-3 md:w-6 md:h-6 text-[#0ECB81] mx-auto mb-0.5 md:mb-2" />
            <p className="text-xs md:text-2xl font-bold font-mono text-[#0ECB81]" data-testid="total-profit-broker">
              847K€
            </p>
            <p className="text-[8px] md:text-xs text-[#848E9C]">Ganancia Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Trades - Responsive */}
      <Card className="card-dark">
        <CardHeader className="border-b border-[#2B3139] py-2 px-3 md:py-4 md:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm md:text-lg text-[#EAECEF]">Operaciones Activas de Andres Martin</CardTitle>
            <Badge variant="outline" className="border-[#0ECB81] text-[#0ECB81] text-[10px] md:text-sm px-1.5 md:px-3 py-0 md:py-1">
              <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#0ECB81] rounded-full mr-1 md:mr-2 animate-pulse" />
              LIVE
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2B3139]">
                  <th className="text-left text-[9px] md:text-xs text-[#848E9C] p-2 md:p-3">Activo</th>
                  <th className="hidden md:table-cell text-center text-xs text-[#848E9C] p-3">Tipo</th>
                  <th className="text-right text-[9px] md:text-xs text-[#848E9C] p-2 md:p-3">Entrada</th>
                  <th className="text-right text-[9px] md:text-xs text-[#848E9C] p-2 md:p-3">Actual</th>
                  <th className="text-right text-[9px] md:text-xs text-[#848E9C] p-2 md:p-3">Ganancia</th>
                  <th className="hidden md:table-cell text-right text-xs text-[#848E9C] p-3">Monto</th>
                  <th className="hidden md:table-cell text-center text-xs text-[#848E9C] p-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {activeTrades.map((trade) => (
                  <tr key={trade.id} className="border-b border-[#2B3139]/50 hover:bg-[#1E2329] transition-colors">
                    <td className="p-2 md:p-3">
                      <div className="flex items-center gap-1.5 md:gap-2">
                        <div className="hidden md:flex w-8 h-8 rounded-full bg-[#0ECB81]/10 items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-[#0ECB81]" />
                        </div>
                        <TrendingUp className="w-3 h-3 text-[#0ECB81] md:hidden" />
                        <div>
                          <span className="text-[11px] md:text-sm font-medium text-[#EAECEF]">{trade.symbol}</span>
                          <p className="hidden md:block text-xs text-[#848E9C]">{trade.coinId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell text-center p-3">
                      <Badge className="bg-[#0ECB81]/20 text-[#0ECB81]">LONG</Badge>
                    </td>
                    <td className="text-right p-2 md:p-3">
                      <span className="text-[10px] md:text-sm font-mono text-[#848E9C]">
                        {trade.entry_price?.toLocaleString('es-ES', {maximumFractionDigits: 0})}€
                      </span>
                    </td>
                    <td className="text-right p-2 md:p-3">
                      <span className="text-[10px] md:text-sm font-mono text-[#EAECEF]">
                        {trade.current_price?.toLocaleString('es-ES', {maximumFractionDigits: 0})}€
                      </span>
                    </td>
                    <td className="text-right p-2 md:p-3">
                      <span className="text-[10px] md:text-sm font-mono font-bold text-[#0ECB81]">
                        +{trade.profit_percent?.toFixed(1).replace('.', ',')}%
                      </span>
                    </td>
                    <td className="hidden md:table-cell text-right p-3 font-mono text-[#EAECEF]">
                      {(trade.id === 1 ? 5000 : trade.id === 2 ? 3200 : trade.id === 3 ? 2500 : 1800).toLocaleString('es-ES')}€
                    </td>
                    <td className="hidden md:table-cell text-center p-3">
                      <Badge variant="outline" className="border-[#0ECB81] text-[#0ECB81]">Activa</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info Card - Responsive */}
      <Card className="card-dark border-[#FCD535]/20">
        <CardContent className="p-3 md:p-6">
          <div className="flex items-start gap-2 md:gap-4">
            <div className="hidden md:flex w-10 h-10 bg-[#FCD535]/10 rounded-lg items-center justify-center flex-shrink-0">
              <Copy className="w-5 h-5 text-[#FCD535]" />
            </div>
            <Copy className="w-4 h-4 text-[#FCD535] md:hidden flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="hidden md:block font-semibold text-[#EAECEF] mb-1">¿Cómo funciona el Copy Trading?</h3>
              <p className="text-[11px] md:text-sm text-[#848E9C]">
                Al activar copy trading, tu capital genera ganancias automáticamente siguiendo las operaciones de Andres Martin.
                <span className="hidden md:inline"> Las ganancias se calculan según la tasa actual de retorno y se acumulan en tu portafolio en tiempo real. ¡Sin hacer nada!</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
